import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/auth.service';
import { API_ENDPOINTS } from '../config/api';
import {
  LayoutDashboard,
  Briefcase,
  Users,
  Calendar,
  BarChart3,
  CreditCard,
  User,
  LogOut,
  Menu,
  X,
  Building2,
  Crown,
  HomeIcon
} from 'lucide-react';

interface ConsultantInfo {
  id: number;
  name: string;
  logo?: string;
  phone_number: string;
  about: string;
  address: string;
  website: string;
  facebook_url?: string;
  linkedin_url?: string;
  user_id: number;
  wilaya_id?: number;
  city?: string;
  company_name?: string;
}

const ConsultantLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [consultant, setConsultant] = useState<ConsultantInfo | null>(null);
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    const loadConsultantInfo = async () => {
      try {
        const consultantInfo = await authService.getTrainingCenterInfo();
        setConsultant(consultantInfo);
      } catch (error) {
      }
    };

    loadConsultantInfo();
  }, []);

  const navigationItems = [
    { icon: LayoutDashboard, label: t('consultant.navigation.dashboard'), path: '/consultant/dashboard' },
    { icon: HomeIcon, label: t('consultant.navigation.backToSite'), path: '/' },
    { icon: Briefcase, label: t('consultant.navigation.services'), path: '/consultant/services' },
    { icon: Users, label: t('consultant.navigation.clients'), path: '/consultant/clients' },
    { icon: Calendar, label: t('consultant.navigation.calendar'), path: '/consultant/calendar' },
    // { icon: BarChart3, label: t('consultant.navigation.stats'), path: '/consultant/stats' },
    { icon: CreditCard, label: t('consultant.navigation.invoices'), path: '/consultant/invoices' },
    { icon: Crown, label: t('consultant.navigation.subscriptions'), path: '/consultant/subscriptions' },
    { icon: User, label: t('consultant.navigation.profile'), path: '/consultant/profile' },
  ];

  const isActivePath = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-30 backdrop-blur-sm transition-opacity duration-300 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
          aria-label={t('consultant.navigation.closeMenu')}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed z-30 inset-y-0 left-0 bg-white shadow-2xl max-h-screen w-64 lg:w-64 transition-all duration-300 border-r border-gray-200 flex flex-col ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {/* Header Sidebar */}
        <div className="flex flex-col items-center py-6 border-b border-gray-200 bg-white">
          <div className="flex items-center space-x-3 mb-3">
            {consultant?.logo ? (
              <img
                src={`${API_ENDPOINTS.UPLOADS}/${consultant.logo}`}
                alt={`${consultant.name} Logo`}
                className="h-12 w-12 rounded-full object-cover border-2 border-brand-primary/20 shadow-md"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div className={`h-12 w-12 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-md ${consultant?.logo ? 'hidden' : ''}`}>
              <User className="h-6 w-6 text-white" />
            </div>
          </div>
          <span className="text-lg font-bold text-gray-900 text-center px-2">
            {consultant?.name || consultant?.company_name || 'Consultant'}
          </span>
          <p className="text-xs text-gray-500 mt-1">{t('consultant.navigation.dashboard')}</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.path);
            
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  if (window.innerWidth < 1024) {
                    setIsSidebarOpen(false);
                  }
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all duration-200 ${
                  isActive
                    ? 'bg-purple-100 text-purple-700 border border-purple-200'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-purple-600'
                }`}
              >
                <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${isActive ? 'text-purple-600' : 'text-gray-500'}`} />
                <span className="font-medium text-sm sm:text-base">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="p-3 border-t border-gray-200 bg-white">
          <div className="flex items-center gap-3 mb-3 p-2 bg-gray-50 rounded-lg">
            <div className="h-8 w-8 sm:h-10 sm:w-10 bg-purple-100 rounded-full flex items-center justify-center">
              <User className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                {user?.fullName || 'Consultant'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email || 'consultant@example.com'}
              </p>
            </div>
          </div>
          
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs sm:text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
          >
            <LogOut className="h-3 w-3 sm:h-4 sm:w-4" />
            {t('consultant.navigation.logout')}
          </button>
        </div>
      </aside>

      {/* Hamburger button - Mobile only */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="fixed top-4 left-4 z-50 p-2 text-gray-500 hover:text-gray-600 focus:outline-none rounded-lg border border-transparent hover:border-gray-200 bg-white/90 shadow-md transition-all lg:hidden"
        aria-label={isSidebarOpen ? t('consultant.navigation.closeMenu') : t('consultant.navigation.openMenu')}
      >
        {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-64">
        {/* Top Navbar */}
        <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6">
            <div className="flex items-center gap-4">
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                {navigationItems.find(item => isActivePath(item.path))?.label || 'Consultant'}
              </h1>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="hidden lg:flex items-center gap-2 text-sm text-gray-600">
                <span>{consultant?.name || consultant?.company_name || 'Consultant'}</span>
                <span>•</span>
                <span>{user?.fullName || 'Consultant'}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-4 sm:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default ConsultantLayout;
